<template>
    <v-field :has-error="hasError" :error-label="errorLabel" :helper="helper" :icon="fieldIcon">
        <q-select ref="field"
                  v-model="inputValue"
                  :clearable="clearable"
                  :filter="filter"
                  autofocus-filter
                  :filter-placeholder="`${$T('type to search')} (${itemsAvailable})`"
                  :name="path"
                  :float-label="label | capitalize"
                  :options="computedOptions"
                  :color="color"
                  :disable="form.busy || disable"
                  @termsChanged="onTermsChanged"
                  @input="onInput"
                  :before="before">
        </q-select>
    </v-field>
</template>

<script>
    import mixins from './mixins'
    import axios from 'axios'
    import {debounce, QSelect, QSpinner} from 'quasar-framework'

    // Add a watcher to QSelect component on "created" event. This watcher emits a "termsChanged" event when
    // the internal var "terms" changes allowing us to hook on that event and sync our local "terms" var
    let QSelectExtended = {
        mixins: [QSelect],

        watch: {
            terms: function (newVal) {
                this.$emit('termsChanged', newVal)
            }
        }
    }

    export default {
        name: 'VAutocomplete',

        mixins,

        props: {
            resourcePath: {
                type: String,
                required: true
            },
            map: {
                type: Function,
                default: (item) => ({label: item.title, value: item.id})
            },
            top: {
                type: Number,
                default: 10
            },
            perPage: {
                type: Number,
                default: 10
            },
            itemPlural: {
                type: String,
                default: function() {
                    return this.$t('items')
                }
            },
            multiple: Boolean,
            chips: Boolean,
            before: Array,
            fieldIcon: String
        },

        components: {
            QSelect: QSelectExtended,
            QSpinner
        },

        data() {
            return {
                isLoading: false,
                terms: '',
                serverData: {
                    data: [],
                    meta: {}
                },
                originalData: {
                    data: [],
                    meta: {}
                },
                topOptions: [],
                inputValue: this.multiple && !(this.value instanceof Array) ?
                    (this.value ? [this.value] : []) :
                    this.value
            }
        },

        watch: {
            terms: 'update',
            'resourcePath'() {
                this.originalData.data = []
                this.processOriginalData()
                this.update()
            }
        },

        mounted() {
            this.update()
        },

        computed: {
            computedOptions() {
                var options = []
                if (this.isLoading)
                    options = [{value: '#', label: `${this.$T('loading')}...`, disable: true}]
                else if (!this.terms && this.topOptions.length > 0)
                    options = [{value: '#', label: this.$T('frequent/recent options:'), disable: true}]
                        .concat(this.topOptions)
                else if (!this.terms)
                    options = [{
                        value: '#', label: this.$_T('not_available_items', {items: this.itemPlural}),
                        disable: true
                    }]
                else
                    options = [{value: '#', label: this.itemsAvailable, sublabel: this.needsNarrow, disable: true}]
                        .concat(this.serverData.data)
                return this.addSelectedTo(options)
            },

            searchResults() {
                return `${
                    this.serverData.meta ? this.serverData.meta.total : this.$t('not_available')
                    } ${this.niceItems}`
            },

            needsNarrow() {
                if (this.serverData.meta.total > this.perPage)
                    return this.$_T('showing :per_page. narrow it changing your search.', this.serverData.meta)
                if (this.serverData.meta.total == 0)
                    return this.$_T('no results for :terms.', {terms: this.terms})
            },

            itemsAvailable() {
                return `${
                    this.serverData.meta ? this.serverData.meta.total_unfiltered : this.$t('not_available')
                    } ${this.niceItems} ${this.$t('available_plural')}`
            },

            niceItems() {
                return this.itemPlural ? this.itemPlural : this.$t('items')
            }
        },

        methods: {
            filter: () => true,

            update() {
                if (!this.terms && this.topOptions.length > 0) return

                this.isLoading = true
                let query = {
                    search: this.terms,
                    per_page: this.perPage
                }
                axios.get(`${config.appUrl}/api/${this.resourcePath}`, {params: query})
                    .then(response => {
                        this.isLoading = false
                        this.originalData = response.data
                        this.processOriginalData()
                    })
                    .catch(() => {
                        this.isLoading = false
                    })
            },

            processOriginalData() {
                let results = deepCopy(this.originalData)
                results.data = results.data.map(this.map)
                if (results.data && results.data.length == 1) {
                    this.inputValue = results.data[0].value
                    this.onInput()
                    this.$refs.field.hide()
                }
                if (results.data) {
                    if (this.topOptions.length == 0)
                        this.topOptions = results.data.slice(0, this.top)

                    results.data = this.addSelectedTo(results.data)
                    this.serverData = results
                    this.$nextTick(() => {
                        if (this.$refs.field) this.$refs.field.reposition()
                    })
                }
            },

            addSelectedTo(options) {
                if (this.multiple) {
                    if (this.serverData.data.length == 0)
                        return options.concat(this.value.map(value => ({
                            value,
                            label: `${this.$T('loading')}...`
                        })))
                    return options.filter(opt => this.value.indexOf(opt.value) == -1)
                        .concat(this.serverData.data.filter(opt => this.value.indexOf(opt.value) >= 0))
                } else if (!options.find(opt => opt.value == this.value) && this.value) {
                    if (this.serverData.data.length == 0)
                        return options.concat([{
                            value: this.value,
                            label: `${this.$T('loading')}...`
                        }])
                    return [this.serverData.data.find(opt => opt.value == this.value)].concat(options)
                }
                return options
            },

            onTermsChanged: debounce(function (terms) {
                this.terms = terms
            }, 300)
        }
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/form/VAutocomplete.vue